import { cloneDeep, isEqual, mapValues } from "lodash";
import { buildAssetUrl } from "./Assets";
import { isURL } from "widgets/forms/validators/validators";

export function addLibraryIdToVideos(formVals, libraryId) {
	let libraryIdRegex = new RegExp(`^${libraryId}\/.+`)
		, newVals = cloneDeep(formVals)
		, x = null
		;

	if (!libraryId) {
		throw Error("Missing library Id");
	}

	// text assets have form values that are only needed for the UI and cannot be sent to the endpoint, this strips them out.
	for (x in newVals) {
		if (typeof (newVals[x]) !== "object") {
			delete newVals[x];
		}
	}

	Object.keys(newVals).forEach((variableName) => {
		let variable = newVals[variableName];
		if (variable.type === 'Video' && !libraryIdRegex.test(variable.data)) {
			newVals[variableName] = Object.assign({}, variable, { data: `${libraryId}/${variable.data}` });
		}
	});

	return newVals;
}

export function serializeVariables(formVals = {}, initialValues = {}, libraryId){
	/*
		This performs some cleanup on our Version (Variables) edit form values to prepare it
		for the API.

		`formVals` keys may include some special formatting to avoid redux-form treating them as paths,
		i.e. for variables with periods in their names, and we need to ensure that the API payload
		has them keyed correctly with the periods.  We just use whatever's in the value's `name` property.
	*/
	if(!libraryId){
		throw Error("Missing library Id");
	}

	const libraryIdRegex = new RegExp(`^${libraryId}\/.+`);

	const newVals = Object.entries(formVals).reduce((prev, [fieldName, variable]) => {
		if(typeof(variable) !== "object"){
			/*
				Text variable editors add a "__Formatted" field to the form with string (i.e. non-object) values,
				which are for UI use only.  We don't add them to the serialized variables, so we just return prev
				to continue.
			*/
			return prev;
		}

		const { type, data, name } = variable || {};
		const canvas = variable?.cropMetadata?.canvas;
		const initialCanvas = initialValues[fieldName]?.cropMetadata?.canvas; /*
			It's important that we use the `fieldName` rather than `variable.name to find the corresponding
			initial value for the entry in `formVals`.
		*/
		const isManualUrl = !!data && !isURL(data);
		const hasCanvasChanged = canvas && !isEqual(canvas, initialCanvas);

		if(type === "Video" && !libraryIdRegex.test(data) && !isManualUrl){
			prev[name] = {
				...variable,
				data: `${libraryId}/${data}`,
			};
		} else if(type === "Image") {
			const nextVariable = {
				...variable,
			};

			if(isManualUrl){
				nextVariable.prop = data;
			}

			if(hasCanvasChanged){
				nextVariable.cropMetadata = {
					...variable.cropMetadata,
					canvas: mapValues(canvas, Math.round),
				};
				nextVariable.prop = isManualUrl ? data : buildAssetUrl(libraryId, variable.data);
			}

			prev[name] = nextVariable;
		} else {
			prev[name] = { ...variable };
		}

		return prev;
	}, {});

	return newVals;
}

export function getVariablesForSave(formValues, initialValues, libraryId) {
	// Round numbers in canvas metadata as API expects integers
	for (const key in formValues) {
		if (formValues[key].type === "Image"
			&& formValues[key].cropMetadata?.canvas
			&& !isEqual(formValues[key].cropMetadata.canvas, initialValues[key].cropMetadata.canvas)) {
			const currentValue = formValues[key]
				, { cropMetadata } = currentValue
				, { canvas } = cropMetadata
				, roundedCanvas = {
					left: Math.round(canvas.left),
					top: Math.round(canvas.top),
					width: Math.round(canvas.width),
					height: Math.round(canvas.height),
				};
			cropMetadata.canvas = roundedCanvas;
			// Return prop to initial url format
			formValues[key].prop = buildAssetUrl(libraryId, formValues[key].data);
		}
	}

	return getVariables(formValues, libraryId);
}

export function getVariables(formValues, libraryId) {
	return Object.values(addLibraryIdToVideos(formValues, libraryId));
}

export function getVariableList(formValues, initialValues, libraryId){
	/*
		When we're saving on the Version Variables endpoint, it expects the Variables to just
		be an array, as opposed to when we're saving a full Version, where it expects a hash
		keyed by variable name.

		For the Version Variables endpoint, we can just use the Object.values of the hash.
	*/
	return Object.values(serializeVariables(formValues, initialValues, libraryId));
}

/**
 * [flattenMap description]
 * @param  {object} socialValues Social Values as they exist on the creative level
 * @param  {object} variableMap  Key/Value pair of variable names and their detail object info
 * @return {object}              New object which contains the socialValue level info and the version info
 * 		We need the Creative Level information to enhance the Version Variable edit view
 * 		However, the data across the creative & social levels do not map 1:1
 * 			We need to marry the version variable data to the creative level data
 *
 * The variable list is returned as an array of objects:

[{
	name: "product",
	data: "shuttle-zoomed.jpg",
	prop: "",
	type: "Image",
	assetId: 123,
	filesize: 252125,
	isFormatted: false,
	required: true
}]

We take the array of objects, and build a key/value object of each variable based on the `name`:
variableMap = {
	product:{
		name: "product",
		data: "shuttle-zoomed.jpg",
		prop: "",
		type: "Image",
		assetId: 123,
		filesize: 252125,
		isFormatted: false,
		required: true
	}
}

The socialValues is returned as an object of objects.

socialValues = {
	image: {
		id: 456,
		name: "SR_17_1920x1080.jpg",
		src: "/crm/v1/asset-manager/SR_17_1920x1080.jpg",
		lastModified: "2024-04-24T19:53:00.000+0000",
		isDynamic: true,
		dynVarName: "product"
	}
}

In order to build the "reset" side of the UI, we need a way to map the socialValue to the variable.

	`flattenMap()`

returns an object of the variables with the current version’s settings, and adds a mapping property:

return {
	product:{
		name: "product",
		data: "shuttle-zoomed.jpg",
		prop: "",
		type: "Image",
		assetId: 123,
		filesize: 252125,
		isFormatted: false,
		required: true,
		mapping:{
			creativeField: "image",
			id: 456,
			name: "SR_17_1920x1080.jpg",
			src: "/crm/v1/asset-manager/SR_17_1920x1080.jpg",
			lastModified: "2024-04-24T19:53:00.000+0000",
			isDynamic: true,
			dynVarName: "product"
		}
	}
}

This includes the `creativeField` which we use to map up for the Creative level information:
	* label
	* tooltip
	* requiredness
	* validation
	* …
`<CreativeLevel />` uses the mapping to render out the:
	* Creative level label
	* the reset to default button
	* the text area showing the default value

PAC and Carousel are only partially supported at the moment intentionally.
Those two types are very unique and probably require a completely separate flow to implement.

 */
export function flattenMap(socialValues, variableMap) {
	let combined = {};

	Object.entries(socialValues).forEach((entries) => {
		let [key, value] = entries
			;

		if (key === "cards") {
			value.forEach((card) => {
				let flatCard = flattenMap(card, variableMap);

				Object.entries(flatCard).forEach((cardValue) => {
					let [cardKey, cardVal] = cardValue;

					combined[cardKey] = {
						...cardVal,
					};
				});
			});

			return;
		}

		if (key === "placements") {
			value?.forEach((place) => {
				let flatPlace = flattenMap(place, variableMap);

				Object.entries(flatPlace).forEach((placeValue) => {
					let [placeKey, placeVal] = placeValue;

					combined[placeKey] = {
						...placeVal,
					};
				});
			});

			return;
		}

		if (variableMap[value?.dynVarName]) {
			combined[value.dynVarName] = {
				...variableMap[value.dynVarName],
				mapping: {
					creativeField: key,
					...value,
				},
			};
		}	//end if(variableMap[value.dynVarName])
	});	//end Object.entries.forEach()

	return combined;
}

export function flattenMap2(socialValues, variableMap) {
	let socialValuesMap = {};

	Object.entries(socialValues).forEach((entries) => {
		let [key, value] = entries;

		if (key === "cards") {
			socialValuesMap.cards = value.map((card) => {
				return flattenMap2(card, variableMap);
			});
			return;
		}

		let { dynVarName } = value || {};

		if (!dynVarName) { return; }

		socialValuesMap[dynVarName] = {
			...value,
			creativeField: key,
		};
	});	//end Object.entries.forEach()

	let combined = {};

	Object.entries(variableMap).forEach((v) => {
		let [key, value] = v;

		combined[key] = {
			...value,
			mapping: socialValuesMap[key],
		};
	});

	return combined;
}
